import {
  Typography,
  List,
  ListItem,
  IconButton,
  ListItemText
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { makeStyles } from 'tss-react/mui'
import { useAuth } from 'components/AuthProvider'
import Button from 'components/Button'
import { addAutoInvestPath } from 'components/Routes'
import DefaultLayout from 'components/layout/DefaultLayout'
import { useMutateDeleteAutoInvest } from 'hooks/useAutoInvest'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { compact } from 'lodash'
import { formatWeightAsGrams } from 'utils/formatWeightAsGrams'

const useStyles = makeStyles()(theme => ({
  cardWrapper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: '4px',
    borderStyle: 'solid',
    borderWidth: 0.5,
    borderColor: theme.palette.primary.main
  }
}))

interface AutoInvestItem {
  id: string | number
  amount?: string | number
  duration?: string
  investBy?: string
  nextAutoinvestDate?: string | null
  product?: {
    name?: string
    [key: string]: unknown
  }
}

interface AutoInvestListItemProps {
  autoInvest: AutoInvestItem
}

function AutoInvestListItem({ autoInvest }: AutoInvestListItemProps) {
  const { classes } = useStyles()
  const { mutate: deleteAutoInvest } = useMutateDeleteAutoInvest()

  const handleCancelAutoInvest = useCallback(
    (autoInvestId: number) => {
      deleteAutoInvest(autoInvestId)
    },
    [deleteAutoInvest]
  )

  const autoInvestStringParts = () => {
    const array: string[] = []

    if (autoInvest.product?.name) {
      array.push(`Buy ${autoInvest.product?.name}`)
    }

    if (autoInvest.amount && autoInvest.investBy) {
      array.push(
        autoInvest.investBy === 'dollar'
          ? `$${autoInvest.amount}`
          : formatWeightAsGrams(Number(autoInvest.amount))
      )
    }

    if (autoInvest.duration) {
      const durationLabels: Record<string, string> = {
        weekly: 'Weekly',
        fortnightly: 'Fortnightly',
        beginning_of_month: 'Beginning of Month'
      }
      array.push(durationLabels[autoInvest.duration] ?? autoInvest.duration)
    }

    return compact(array)
  }

  return (
    <ListItem
      key={autoInvest.id}
      className={classes.cardWrapper}
      secondaryAction={
        <IconButton
          edge='end'
          aria-label='delete'
          onClick={() => handleCancelAutoInvest(Number(autoInvest.id))}
        >
          <DeleteIcon />
        </IconButton>
      }
    >
      <ListItemText
        primary={autoInvestStringParts().join(', ')}
        secondary={
          autoInvest.nextAutoinvestDate
            ? `Next investment ${autoInvest.nextAutoinvestDate}`
            : null
        }
      />
    </ListItem>
  )
}

export default function AutoInvest(): JSX.Element {
  const navigate = useNavigate()

  const { user } = useAuth()

  const autoInvests = useMemo(() => user?.autoInvests, [user])

  const goToCreateNewAutoInvestment = useCallback(
    () => navigate(addAutoInvestPath()),
    [navigate]
  )

  return (
    <DefaultLayout heading='Auto invest' showBackIcon>
      <Typography variant='body1' align='center' my={2}>
        Automatically invest on a frequency that suits you.
      </Typography>

      <List>
        {(autoInvests as AutoInvestItem[] | undefined)?.map(autoInvest => (
          <AutoInvestListItem autoInvest={autoInvest} key={autoInvest.id} />
        ))}
      </List>

      <Button
        fullWidth
        variant='outlined'
        onClick={goToCreateNewAutoInvestment}
        sx={{ mb: 6 }}
      >
        CREATE NEW AUTO INVESTMENT
      </Button>
    </DefaultLayout>
  )
}
